import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import classNames from 'classnames';
import ModalAction from '@/components/ModalAction';
import LoaderIcon from '@/images/icons/Loader';
import { fetchAPI } from '@/api';
import { OutletContext } from '@/types';
import ErrorModal from '@/components/ErrorModal';

interface PaymentMethod {
    id: string;
    name: string;
    enabled: boolean;
}

function Stripe({ project, paymentOption, settingModalOpen, setSettingModalOpen, isPaymentOptionFetched }: { project: any, paymentOption: any, settingModalOpen: boolean, setSettingModalOpen: (value: boolean) => void, isPaymentOptionFetched: boolean }) {
    const { auth } = useOutletContext<OutletContext>();
    const location = useLocation();
    const navigate = useNavigate();
    
    const liveMode = paymentOption.id === 'stripeLive';
    const testMode = paymentOption.id === 'stripeTest';
    const initialStripeData = paymentOption.data && Object.keys(paymentOption.data).length > 0 ? paymentOption.data : null;
    
    const [currentStripe, setCurrentStripe] = useState(initialStripeData);
    
    const getSettingsValue = (key: string, defaultValue = false) => {
        return currentStripe && currentStripe[key] !== undefined ? currentStripe[key] : defaultValue;
    };
    
    const [formData, setFormData] = useState({
        couponsEnabled: false,
        automaticTaxEnabled: false,
        taxIDCollectionEnabled: false,
        taxIDRequired: false,
        customizePaymentOptions: false,
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMethods, setIsLoadingMethods] = useState(true);
    const [stripePaymentMethods, setStripePaymentMethods] = useState<PaymentMethod[]>([]);
    const originalPaymentMethodsRef = useRef<PaymentMethod[]>([]);
    const [thanksModalOpen, setThanksModalOpen] = useState(false);
    const [isStripeConnectedSuccess, setIsStripeConnectedSuccess] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModal, setErrorModal] = useState('');
    const [errorValidation, setErrorValidation] = useState('');
    const [connectStripeModalOpen, setConnectStripeModalOpen] = useState(false);
    const [connectStripeUrl, setConnectStripeUrl] = useState('');
    const [isConnectStripeLoading, setIsConnectStripeLoading] = useState(false);
    const [paymentMethodsFetchingError, setPaymentMethodsFetchingError] = useState(false);
    const [isStripeConnected, setIsStripeConnected] = useState(paymentOption.isActive && paymentOption.isAvailable);
    const [isRefreshLoading, setIsRefreshLoading] = useState(false);
    const [isConnectingStripeUrl, setIsConnectingStripeUrl] = useState(false);

    const onClearModal = () => {
        if (originalPaymentMethodsRef.current.length > 0) {
            setStripePaymentMethods([...originalPaymentMethodsRef.current]);
            
            // Set settings, keeping the status of customizePaymentOptions
            setFormData({
                couponsEnabled: getSettingsValue('couponsEnabled'),
                automaticTaxEnabled: getSettingsValue('automaticTaxEnabled'),
                taxIDCollectionEnabled: getSettingsValue('taxIDCollectionEnabled'),
                taxIDRequired: getSettingsValue('taxIDRequired'),
                customizePaymentOptions: getSettingsValue('paymentMethodConfig')?.active === true,
            });
        } else {
            // If there are no original methods, just reset the settings
            setFormData({
                couponsEnabled: getSettingsValue('couponsEnabled'),
                automaticTaxEnabled: getSettingsValue('automaticTaxEnabled'),
                taxIDCollectionEnabled: getSettingsValue('taxIDCollectionEnabled'),
                taxIDRequired: getSettingsValue('taxIDRequired'),
                customizePaymentOptions: false,
            });
        }
        
        setSettingModalOpen(false);
        setErrorModal('');
    };
    
    const onFieldChange = (field: string, value: any) => {
        let updatedFormData = {
            ...formData,
            [field]: value
        };

        // If "Require tax ID" is being enabled, automatically enable "Enable tax ID collection"
        if (field === 'taxIDRequired' && value === true) {
            updatedFormData.taxIDCollectionEnabled = true;
        }

        // If "Enable tax ID collection" is being disabled, also disable "Require tax ID"
        if (field === 'taxIDCollectionEnabled' && value === false) {
            updatedFormData.taxIDRequired = false;
        }

        setFormData(updatedFormData);
    };
    
    const onPaymentMethodChange = (methodId: string, enabled: boolean) => {
        const updatedMethods = stripePaymentMethods.map((method) => 
            method.id === methodId ? {...method, enabled: enabled} : method
        );
        setStripePaymentMethods(updatedMethods);

        const hasAtLeastOneEnabled = updatedMethods.some(method => method.enabled);
        if (hasAtLeastOneEnabled && errorValidation) {
            setErrorValidation('');
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            if (formData.customizePaymentOptions) {
                const hasEnabledMethod = stripePaymentMethods.some(method => method.enabled);
                if (!hasEnabledMethod) {
                    setErrorValidation('Please enable at least one payment method');
                    setIsLoading(false);
                    return;
                }
            }

            const requestData = {
                projectId: project.projectId,
                liveMode: liveMode,
                couponsEnabled: formData.couponsEnabled,
                automaticTaxEnabled: formData.automaticTaxEnabled,
                taxIDCollectionEnabled: formData.taxIDCollectionEnabled,
                taxIDRequired: formData.taxIDRequired
            } as any;

            // If customizePaymentOptions is enabled, add the payment methods configuration
            if (formData.customizePaymentOptions) {
                const enabledMethods = {} as any;
                
                stripePaymentMethods.forEach(method => {
                    enabledMethods[method.id] = method.enabled;
                });
                
                if (Object.keys(enabledMethods).length > 0) {
                    requestData.enabledMethods = enabledMethods;
                }
            }

            const response = await fetchAPI("/project/payment-options/stripe-settings-update", auth.user.token, requestData);
            
            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Update currentStripe with the returned settings
            if (data && data.stripeSettings) {
                setCurrentStripe(data.stripeSettings);
                navigate(location.pathname + location.search, { state: {
                    ...location.state,
                    data: data.stripeSettings,
                }, replace: true });
            }
            
            setErrorModal('');
            setIsStripeConnectedSuccess(false);
            setThanksModalOpen(true);
            setSettingModalOpen(false);
            setErrorValidation('');
        } catch (error: any) {
            setErrorModal(error.message || 'Failed to save settings');
            setErrorModalOpen(true);
            setSettingModalOpen(false);
            setErrorValidation('');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchStripePaymentMethods = useCallback(async () => {
        if (!currentStripe) {
            return;
        }

        try {
            setIsLoadingMethods(true);
            setPaymentMethodsFetchingError(false);
            
            const response = await fetchAPI("/project/payment-options/stripe-payment-methods", auth.user.token, {
                projectId: project.projectId,
                liveMode: !paymentOption.isTest,
            });
            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }
      
            const data = await response.json();
        
            if (data) {
                const availableMethods = data;
                const methods = availableMethods.map((method: any) => {
                    return {
                        id: method.id,
                        name: method.name,
                        enabled: method.enabled,
                    };
                });
                
                originalPaymentMethodsRef.current = [...methods];

                setStripePaymentMethods(methods);
            }
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            setPaymentMethodsFetchingError(true);
        } finally {
            setIsLoadingMethods(false);
        }
    }, [auth.user.token, paymentOption, project.projectId, currentStripe]);

    const fetchStripeRefresh = useCallback(async () => {
        try {
            setIsRefreshLoading(true);
            setErrorModal('');
            
            const response = await fetchAPI("/project/payment-options/stripe-refresh", auth.user.token, {
                projectId: project.projectId,
                liveMode: liveMode,
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data) {
                window.location.reload();
            }
        } catch (error: any) {
            console.error('Error refreshing Stripe data:', error);
            setErrorModal(error.message || 'Failed to refresh Stripe data');
            setErrorModalOpen(true);
        } finally {
            setIsRefreshLoading(false);
        }
    }, [auth.user.token, project.projectId, liveMode]);

    const fetchConnectStripeUrl = useCallback(async () => {
        if (!currentStripe) {
            try {
                setIsConnectStripeLoading(true);
                setErrorModal('');
                
                const response = await fetchAPI('/project/payment-options/stripe-connect', auth.user.token, {
                    projectId: project.projectId,
                    liveMode: liveMode
                });
                
                if (!response.ok) {
                    throw new Error('Failed to get Stripe Connect URL. Please try again.');
                }
                
                const data = await response.json();
                
                if (data && data.url) {;
                    setConnectStripeUrl(data.url);
                    setErrorModal('');
                    setConnectStripeModalOpen(true);
                } else {
                    throw new Error('Invalid response from server');
                }
            } catch (error: any) {
                setErrorModal(error.message || 'Failed to connect Stripe');
                setErrorModalOpen(true);
                setConnectStripeModalOpen(false);
            } finally {
                setIsConnectStripeLoading(false);
            }
        }
    }, [auth.user.token, project.projectId, liveMode, currentStripe]);

    useEffect(() => {
        if (isPaymentOptionFetched) {
            fetchStripePaymentMethods();
        }
    }, [isPaymentOptionFetched]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const hasSuccess = urlParams.has('success');

        if (hasSuccess) {
            const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            
            setIsStripeConnectedSuccess(true);
            setThanksModalOpen(true);
        }
    }, []);

    useEffect(() => {
        if (connectStripeModalOpen && !currentStripe) {
            fetchConnectStripeUrl();
        }
    }, [connectStripeModalOpen, currentStripe, fetchConnectStripeUrl]);

    useEffect(() => {
        if (currentStripe) {
            setFormData({
                couponsEnabled: getSettingsValue('couponsEnabled'),
                automaticTaxEnabled: getSettingsValue('automaticTaxEnabled'),
                taxIDCollectionEnabled: getSettingsValue('taxIDCollectionEnabled'),
                taxIDRequired: getSettingsValue('taxIDRequired'),
                customizePaymentOptions: getSettingsValue('paymentMethodConfig')?.active === true,
            });
        }
    }, [currentStripe]);

    useEffect(() => {
        setIsStripeConnected(paymentOption.isActive && paymentOption.isAvailable);
    }, [paymentOption]);

    useEffect(() => {
        const newStripeData = paymentOption.data && Object.keys(paymentOption.data).length > 0 ? paymentOption.data : null;
        setCurrentStripe(newStripeData);
    }, [paymentOption.data]);

    useEffect(() => {
        if (errorValidation) {
            setErrorValidation('');
        }
    }, [formData.customizePaymentOptions]);

    return (
        <>
            <div>
                {!isStripeConnected && (
                    <>
                        <div>With Stripe, you'll be able to accept subscription payments from customers anywhere in the world.</div>
                        <div>Stripe is available for businesses in <a className="underline hover:no-underline" href="https://stripe.com/global" target="_blank" rel="noopener noreferrer">42 countries.</a></div>
                        <button className="btn-primary mt-8 mb-4" onClick={() => setConnectStripeModalOpen(true)}>Connect Stripe</button>
                    </>
                )}
                {isStripeConnected && !currentStripe && (
                    <>
                        <div>Please click <b>Refresh</b> to load your country and available account information.</div>
                        <button className="btn-primary mt-8 mb-4 min-w-[140px]" onClick={() => fetchStripeRefresh()}>
                            {isRefreshLoading ? (
                                <div className="flex items-center justify-center py-1 pointer-events-none">
                                    <LoaderIcon className="animate-rotation w-6 h-6 text-white" />
                                </div>
                            ) : 'Refresh Stripe'}
                        </button>
                    </>
                )}
                
                {(currentStripe?.accountId || currentStripe?.country || currentStripe?.automaticTaxEnabled || currentStripe?.couponsEnabled || currentStripe?.taxIDCollectionEnabled || currentStripe?.taxIDRequired) && (
                    <div className="w-full">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-4">
                                {currentStripe?.accountId && (
                                    <div className="flex justify-between">
                                        <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2] text-[#A5A5A5] font-medium min-w-[125px]">Connected account</div>
                                        <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2]">{currentStripe?.accountId && currentStripe?.accountId}</div>
                                    </div>
                                )}
                                {currentStripe?.country && (
                                    <div className="flex justify-between">
                                        <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2] text-[#A5A5A5] font-medium min-w-[125px]">Country</div>
                                        <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2]">{currentStripe?.country && currentStripe?.country}</div>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2] text-[#A5A5A5] font-medium min-w-[125px]">Coupons</div>
                                    <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2]">{currentStripe?.couponsEnabled ? 'Enabled' : 'Disabled'}</div>
                                </div>
                                <div className="flex justify-between">
                                    <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2] text-[#A5A5A5] font-medium min-w-[125px]">Automatic tax</div>
                                    <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2]">{currentStripe?.automaticTaxEnabled ? 'Enabled' : 'Disabled'}</div>
                                </div>
                                <div className="flex justify-between">
                                    <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2] text-[#A5A5A5] font-medium min-w-[125px]">Tax ID collection</div>
                                    <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2]">{currentStripe?.taxIDCollectionEnabled ? 'Enabled' : 'Disabled'}</div>
                                </div>
                                <div className="flex justify-between">
                                    <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2] text-[#A5A5A5] font-medium min-w-[125px]">Tax ID required</div>
                                    <div className="w-1/2 text-[11px] sm:text-[13px]/[1.2]">{currentStripe?.taxIDRequired ? 'Enabled' : 'Disabled'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <ModalAction
                id="stripe-edit-modal"
                modalOpen={settingModalOpen}
                setModalOpen={setSettingModalOpen}
                onModalClose={() => onClearModal()}
                size={undefined}
            >
                <div className="h2 mb-8 md:mb-10">
                    Edit Stripe settings
                </div>
                <div className="block mt-10">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.couponsEnabled}
                            onChange={(e) => onFieldChange('couponsEnabled', !formData.couponsEnabled)}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Enable coupons</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-5">
                        Enable coupons to allow customers to apply discounts to their payments.
                    </div>
                </div>
                <div className="block mt-10">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.automaticTaxEnabled}
                            onChange={(e) => onFieldChange('automaticTaxEnabled', !formData.automaticTaxEnabled)}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Enable automatic tax</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-5">
                        Automatically calculate taxes for payments.
                    </div>
                </div>
                <div className="block mt-10">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.taxIDCollectionEnabled}
                            onChange={(e) => onFieldChange('taxIDCollectionEnabled', !formData.taxIDCollectionEnabled)}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Enable tax ID collection</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-5">
                        Collect tax IDs from customers.
                    </div>
                </div>
                <div className="block mt-10">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.taxIDRequired}
                            onChange={(e) => onFieldChange('taxIDRequired', !formData.taxIDRequired)}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Require tax ID</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-5">
                        Require customers to provide tax IDs.
                    </div>
                </div>
                <div className="block mt-10">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.customizePaymentOptions}
                            onChange={(e) => onFieldChange('customizePaymentOptions', !formData.customizePaymentOptions)}
                            disabled={isLoadingMethods || paymentMethodsFetchingError}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Customize payment options</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-5">
                        Customize default Stripe payment options.
                    </div>
                    
                    {formData.customizePaymentOptions && !paymentMethodsFetchingError && (
                        <div className="mt-6">
                            <div className="text-[14px]/[1.2] font-medium mb-3">Available payment methods</div>
                            {isLoadingMethods ? (
                                <div className="flex items-center justify-center py-1 pointer-events-none mt-6">
                                    <LoaderIcon className="animate-rotation w-6 h-6 text-brand-green" />
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    {stripePaymentMethods.map((method) => (
                                        <label key={method.id} className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={method.enabled}
                                            onChange={() => {
                                                onPaymentMethodChange(method.id, !method.enabled);
                                            }}
                                            disabled={isLoading}
                                        />
                                        <div className="form-toggle" />
                                            <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] capitalize">
                                                {method.name.replace(/_/g, ' ')}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <button className={classNames(
                    `btn-primary w-full mt-12`,
                    (!isLoading && !isLoadingMethods) && 'hidden',
                )}>
                    <div className="flex items-center justify-center py-1 pointer-events-none">
                        <LoaderIcon className="animate-rotation w-6 h-6 text-white" />
                    </div>
                </button>

                <div className={(isLoading || isLoadingMethods) ? "hidden" : "mt-12"}>
                    <button className="btn-primary w-full" onClick={() => handleSave()}>
                        <span className="pointer-events-none">Save</span>
                    </button>
                    {errorValidation && (
                        <div className="text-sm text-red-orange mt-2 font-medium">{errorValidation}</div>
                    )}
                </div>

            </ModalAction>
            <ModalAction
                id="thanks-settings-modal"
                modalOpen={thanksModalOpen}
                setModalOpen={setThanksModalOpen}
                size="sm"
            >
                <h2 className="h2 mb-4 pr-16 md:pr-20 leading-[0.9]">
                    {isStripeConnectedSuccess ? 'Stripe connected successfully!' : 'Settings saved successfully'}
                </h2>
                <div>
                    <button
                        className="btn-primary w-full mt-8"
                        onClick={() => setThanksModalOpen(false)}
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center py-1 pointer-events-none">
                                <LoaderIcon className="animate-rotation w-6 h-6 text-white" />
                            </div>
                        ) : (
                            <span className="pointer-events-none">Okay</span>
                        )}
                    </button>
                </div>
                {errorModal && (
                    <div className="text-sm text-red-orange mt-2 font-medium">{errorModal}</div>
                )}
            </ModalAction>
            <ModalAction
                id="connect-stripe-modal"
                modalOpen={connectStripeModalOpen}
                setModalOpen={(value: boolean) => {
                    setConnectStripeModalOpen(value);
                    if (setConnectStripeModalOpen) {
                        setConnectStripeModalOpen(value);
                    }
                }}
                size="sm"
                onModalClose={() => {
                    setConnectStripeModalOpen(false);
                }}
            >
                <h2 className="h2 mb-4">
                    Connect Stripe {liveMode ? 'Live' : 'Test'}
                </h2>
                
                {isConnectStripeLoading ? (
                    <div className="flex items-center justify-center py-4">
                        <LoaderIcon className="animate-rotation w-8 h-8 text-brand-green" />
                    </div>
                ) : (
                    <>
                        <div>
                            By pressing "Connect" you will be redirected to Stripe to connect your account
                        </div>
                        <a href={connectStripeUrl} onClick={() => setIsConnectingStripeUrl(true)} className="btn-primary w-full mt-8">
                            {isConnectingStripeUrl ? (
                                <div className="flex items-center justify-center py-1 pointer-events-none">
                                    <LoaderIcon className="animate-rotation w-6 h-6 text-white" />
                                </div>
                            ) : (
                                <span className="pointer-events-none">Connect</span>
                            )}
                        </a>
                        <button className="btn-primary-inverted w-full mt-4" onClick={() => {
                            setConnectStripeModalOpen(false);
                            setSettingModalOpen(false);
                        }}>
                            <span className="pointer-events-none">Cancel</span>
                        </button>
                    </>
                )}
            </ModalAction>
            <ErrorModal
                modalOpen={errorModalOpen}
                setModalOpen={setErrorModalOpen}
                error={errorModal}
            />
        </>
    );
}

export default Stripe;
